import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  Modal,
  Radio,
  Space,
  Table,
  Typography,
  message
} from 'antd';
import type { RouteViewModel } from '../../types/routeViewModel';
import { isLiveRouteStatus } from '../mutationWorkspace';
import { MutationReasonField } from '../mutationWorkspace/MutationReasonField';
import {
  useGetInjectableApplicationsQuery,
  useInjectApplicationIntoRouteMutation
} from '../../store/api/routesApi';
import { isSequentialStopsReadModeEnabled } from '../sequentialStops/sequentialStopsReadFlags';
import { sortStopsByPosition } from '../sequentialStops/sequentialStopsOrdering';

const { Text } = Typography;

type InjectableRow = {
  id: number;
  internalNumber: string;
  clientName: string | null;
  routeLabel: string;
  cargoSummary: string;
  totalWeightKg: number | null;
  totalVolumeM3: number | null;
  cargoCount: number;
  availableCargoCount: number;
};

type Props = {
  routeId: number;
  view: RouteViewModel;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export function LiveApplicationInjectionWizard({
  routeId,
  view,
  open,
  onClose,
  onSuccess
}: Props) {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [search, setSearch] = useState('');
  const [selectedAppId, setSelectedAppId] = useState<number | null>(null);
  const [insertAfterPosition, setInsertAfterPosition] = useState<number | 'end'>('end');
  const [mutationReason, setMutationReason] = useState<string | null>(null);
  const [confirmLive, setConfirmLive] = useState(false);

  const live = isLiveRouteStatus(view.operation.status);

  const orderedStops = useMemo(() => {
    const stops = view.structure?.stops ?? [];
    return isSequentialStopsReadModeEnabled()
      ? sortStopsByPosition(stops)
      : [...stops].sort((a, b) => Number(a.position) - Number(b.position));
  }, [view.structure?.stops]);

  const { data: injectableApps, isFetching } = useGetInjectableApplicationsQuery(
    { routeId, search: search.trim() || undefined },
    { skip: !open }
  );

  const [inject, { isLoading: injecting }] = useInjectApplicationIntoRouteMutation();

  const reset = () => {
    setStep(0);
    setSearch('');
    setSelectedAppId(null);
    setInsertAfterPosition('end');
    setMutationReason(null);
    setConfirmLive(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const selectedApp = injectableApps?.find((a) => a.id === selectedAppId);

  const runInject = async () => {
    if (selectedAppId == null) return;
    try {
      await inject({
        routeId,
        applicationId: selectedAppId,
        insertAfterPosition: insertAfterPosition === 'end' ? null : insertAfterPosition,
        confirmationProvided: live ? confirmLive : true,
        mutationReason: mutationReason?.trim() || undefined
      }).unwrap();
      message.success('Заявка добавлена в маршрут');
      handleClose();
      onSuccess();
    } catch (err: unknown) {
      const data = (err as { data?: { error?: string } })?.data;
      message.error(data?.error ?? 'Не удалось добавить заявку');
    }
  };

  const columns = [
    {
      title: '№',
      dataIndex: 'internalNumber',
      key: 'num',
      render: (v: string, row: InjectableRow) => (
        <Button type="link" size="small" onClick={() => { setSelectedAppId(row.id); setStep(1); }}>
          {v}
        </Button>
      )
    },
    { title: 'Клиент', dataIndex: 'clientName', key: 'client' },
    { title: 'Маршрут', dataIndex: 'routeLabel', key: 'route' },
    { title: 'Груз', dataIndex: 'cargoSummary', key: 'cargo' },
    {
      title: 'Вес / объём',
      key: 'dims',
      render: (_: unknown, row: InjectableRow) => {
        const w = row.totalWeightKg != null ? `${row.totalWeightKg} кг` : '—';
        const v = row.totalVolumeM3 != null ? `${row.totalVolumeM3} м³` : '—';
        return `${w} / ${v}`;
      }
    }
  ];

  return (
    <Modal
      title="Добавить заявку в маршрут"
      open={open}
      onCancel={handleClose}
      width={720}
      footer={
        step === 0 ? (
          <Button onClick={handleClose}>Закрыть</Button>
        ) : step === 1 ? (
          <Space>
            <Button onClick={() => setStep(0)}>Назад</Button>
            <Button type="primary" disabled={insertAfterPosition === undefined} onClick={() => setStep(2)}>
              Далее
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={() => setStep(1)}>Назад</Button>
            <Button
              type="primary"
              loading={injecting}
              disabled={live && !confirmLive}
              onClick={runInject}
            >
              Добавить в маршрут
            </Button>
          </Space>
        )
      }
      destroyOnClose
    >
      {live ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Маршрут в исполнении"
          description="Добавление заявки изменит будущие точки и аллокации. Подтвердите операцию на последнем шаге."
        />
      ) : null}

      {step === 0 ? (
        <>
          <Input.Search
            placeholder="Поиск по номеру, клиенту, адресу"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <Table<InjectableRow>
            size="small"
            rowKey="id"
            loading={isFetching}
            dataSource={injectableApps ?? []}
            columns={columns}
            pagination={false}
            locale={{ emptyText: 'Нет доступных заявок' }}
          />
        </>
      ) : null}

      {step === 1 && selectedApp ? (
        <>
          <Text>
            Заявка <strong>№{selectedApp.internalNumber}</strong>
            {selectedApp.clientName ? ` — ${selectedApp.clientName}` : ''}
          </Text>
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Куда вставить новую группу в маршруте?</Text>
            <Radio.Group
              style={{ display: 'block', marginTop: 8 }}
              value={insertAfterPosition === 'end' ? 'end' : insertAfterPosition}
              onChange={(e) => {
                const v = e.target.value;
                setInsertAfterPosition(v === 'end' ? 'end' : Number(v));
              }}
            >
              {orderedStops.map((s, idx) => (
                <Radio
                  key={s.id != null ? String(s.id) : `pos-${idx}`}
                  value={Number(s.position)}
                  style={{ display: 'block', margin: '4px 0' }}
                >
                  <span>
                    После точки #{idx + 1} — {s.type === 'loading' ? 'Погрузка' : 'Выгрузка'}{' '}
                    {String(s.city || s.address || '')}
                  </span>
                </Radio>
              ))}
              <Radio value="end" style={{ display: 'block', marginTop: 8 }}>
                В конец маршрута
              </Radio>
            </Radio.Group>
          </div>
        </>
      ) : null}

      {step === 2 ? (
        <>
          {live ? <MutationReasonField value={mutationReason} onChange={setMutationReason} /> : null}
          {live ? (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 12 }}
              message="Подтверждение"
              description={
                <label style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={confirmLive}
                    onChange={(e) => setConfirmLive(e.target.checked)}
                    style={{ marginRight: 8 }}
                  />
                  Я подтверждаю добавление заявки в маршрут в исполнении
                </label>
              }
            />
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}
